import {IShapeDiverParameter} from "@AppBuilderLib/entities/parameter/config/parameter";
import {useShapeDiverStoreParameters} from "@AppBuilderLib/entities/parameter/model/useShapeDiverStoreParameters";
import {useShapeDiverStoreViewport} from "@AppBuilderLib/entities/viewport/model/useShapeDiverStoreViewport";
import {addListener, EVENTTYPE_SESSION} from "@shapediver/viewer.session";
import {indirizzoDiPartenza} from "./EidosMappaComponent";
import {inquadraTotem, zoomDedicaOff} from "./zoomDedica";
/**
 * TOTEM EIDOS — comportamenti per lo schermo touch del negozio (vedi anche totem.css).
 *
 * 1) Se l'indirizzo della pagina contiene ?totem=1, il body prende la classe "eidos-totem": le regole
 *    di totem.css si applicano qualunque sia lo schermo (utile anche per provare dal computer).
 * 2) Ritorno all'inizio: sul totem, dopo alcuni minuti senza tocchi, la pagina si ricarica da sola,
 *    cosi' il cliente successivo trova il configuratore pulito (indirizzo di partenza, nessuna dedica...).
 *    Minuti: ?totem=1 -> 10 minuti (predefinito); ?totem=5 -> 5 minuti; ?totem=0 -> classe si', ritorno no.
 *    Il ritorno NON ricarica la pagina (ogni ricarica = una sessione ShapeDiver = crediti): rimette i parametri.
 * 3) RICONOSCIMENTO AUTOMATICO (26 set 2026): anche SENZA ?totem, se lo schermo e' verticale, largo fra
 *    700 e 900 px CSS ed e' touch (= il totem del negozio), il body prende la classe "eidos-totem": serve
 *    quando il configuratore gira dentro il sito WooCommerce, dove non possiamo aggiungere ?totem all'indirizzo.
 *    Anche in quel caso, dopo 10 minuti senza tocchi, i parametri tornano a quelli iniziali.
 * Sul computer e sul telefono non succede niente.
 */
/** minuti senza tocchi dopo i quali il totem torna allo stato di partenza (Davide, 26 set 2026: 10) */
const MINUTI_PREDEFINITI = 10;

/**
 * RITORNO ALLO STATO DI PARTENZA SENZA RICARICARE LA PAGINA (27 set 2026).
 * Ricaricare la pagina apriva ogni volta una NUOVA SESSIONE ShapeDiver, e le sessioni consumano i crediti del
 * piano: con il totem acceso tutto il giorno erano decine di sessioni al giorno senza nessun cliente.
 * Ora, dopo l'inattivita', si rimettono i parametri ai valori iniziali nella STESSA sessione (un solo calcolo,
 * e solo se qualcosa era stato cambiato), si chiude la tastiera e si torna alla vista intera.
 */
export function ripartiDaCapo(): void {
	let cambiati = 0;
	try {
		const store = useShapeDiverStoreParameters.getState();
		const {parameterStores} = store;
		// ⚠️ BUG corretto il 28 set (sera): prima si cambiava il valore di TUTTI i parametri ma si eseguiva
		// (execute) solo l'ULTIMO: il visore riceveva un parametro solo e gli altri restavano "sospesi" (menu
		// a posto, gioiello vecchio: "la forma non corrisponde", e le scelte successive si incastravano).
		// Ora si usa la funzione dell'SDK che manda TUTTI i valori in un unico calcolo (batchParameterValueUpdate),
		// solo per i parametri che sono davvero diversi dal valore iniziale.
		const daRimettere: Record<string, Record<string, unknown>> = {};
		for (const ns of Object.keys(parameterStores)) {
			for (const st of Object.values(parameterStores[ns])) {
				const p = st.getState() as IShapeDiverParameter<unknown>;
				const def = p.definition.defval;
				if (def === undefined || def === null) continue;
				const diversoUi = String(p.state.uiValue ?? "") !== String(def);
				const diversoModello = String(p.state.execValue ?? "") !== String(def);
				if (!diversoUi && !diversoModello) continue;
				(daRimettere[ns] ??= {})[p.definition.id] = def;
				cambiati++;
			}
		}
		// l'indirizzo torna alle coordinate del punto di partenza, non al testo iniziale del modello
		const ip = indirizzoDiPartenza();
		if (ip) {
			for (const ns of Object.keys(parameterStores)) {
				const st = parameterStores[ns][ip.id];
				if (!st) continue;
				const p = st.getState() as IShapeDiverParameter<unknown>;
				const giaLi = String(p.state.execValue ?? "") === ip.testo && String(p.state.uiValue ?? "") === ip.testo;
				if (daRimettere[ns]?.[ip.id] !== undefined || !giaLi) {
					(daRimettere[ns] ??= {})[ip.id] = ip.testo;
					cambiati++;
				} else if (daRimettere[ns]) {
					delete daRimettere[ns][ip.id];
				}
			}
		}
		if (cambiati > 0) void store.batchParameterValueUpdate(daRimettere, true, true);
	} catch {
		/* store non pronto */
	}
	try {
		(document.activeElement as HTMLElement | null)?.blur?.();
		document.body.classList.remove("eidos-tastiera-aperta");
		zoomDedicaOff(1.55);
	} catch {
		/* ignora */
	}
	if (cambiati === 0) {
		// niente da rimettere a posto: solo la vista. Sul totem l'inquadratura e' la nostra (5 ott: prima si usava
		// lo zoomTo del visore, che riprendeva la direzione lasciata dall'ultimo cliente)
		if (document.body.classList.contains("eidos-totem")) {
			inquadraTotem(true);
			return;
		}
		for (const vp of Object.values(useShapeDiverStoreViewport.getState().viewports)) {
			try {
				(vp.camera as unknown as {zoomTo?: () => void}).zoomTo?.();
			} catch {
				/* ignora */
			}
		}
	}
}

function leggiTotem(): number | null {
	try {
		const v = new URLSearchParams(window.location.search).get("totem");
		if (v === null) return null;
		const n = parseFloat(v);
		if (isNaN(n)) return MINUTI_PREDEFINITI;
		// ?totem=1 vuol dire "totem acceso" (10 minuti); ?totem=0 niente ritorno; ?totem=N (N>1) = N minuti
		// ⚠️ 28 set: prima ?totem=1 valeva 1 MINUTO -> il cliente che scriveva la dedica si vedeva tutto azzerato
		if (n === 1) return MINUTI_PREDEFINITI;
		return n;
	} catch {
		return null;
	}
}

/** schermo verticale largo fra 600 e 1000 px CSS: e' il totem (sul computer una finestra cosi' non capita) */
const MEDIA_TOTEM = "(orientation: portrait) and (min-width: 600px) and (max-width: 1000px)";

function sembraTotem(): boolean {
	try {
		return window.matchMedia(MEDIA_TOTEM).matches;
	} catch {
		return false;
	}
}

/**
 * (24 ott 2026, Davide: «limitalo ai veri totem») VERO TOTEM = l'indirizzo ha ?totem=... (il totem del negozio oggi)
 * oppure il browser si presenta come "EidosTotem" (da impostare nello user agent di Fully Kiosk quando il totem
 * mostrera' il sito WooCommerce: dentro l'iframe del negozio ?totem non si puo' aggiungere). Solo il vero totem ha la
 * tastiera Eidos, il ritorno all'inizio dopo 10 minuti e il cane da guardia che ricarica la pagina. Un iPad o un tablet
 * qualunque tenuto in verticale prende solo la DISPOSIZIONE del totem (classe "eidos-totem", che segue lo schermo).
 */
export function totemVero(): boolean {
	try {
		if (new URLSearchParams(window.location.search).has("totem")) return true;
		return /EidosTotem/i.test(navigator.userAgent || "");
	} catch {
		return false;
	}
}

/** senza ?totem: la classe segue lo schermo (anche se il totem viene girato o la finestra cambia) */
function segui(): void {
	const aggiorna = () => {
		if (sembraTotem()) document.body.classList.add("eidos-totem");
		else document.body.classList.remove("eidos-totem");
	};
	aggiorna();
	try {
		window.matchMedia(MEDIA_TOTEM).addEventListener("change", aggiorna);
	} catch {
		window.addEventListener("resize", aggiorna);
	}
}

export function avviaTotem(): void {
	// per le prove dal browser: window.__eidosTotem.riparti()
	(window as unknown as {__eidosTotem: unknown}).__eidosTotem = {riparti: ripartiDaCapo, avviso: mostraAvvisoRicarica, ricariche: ricaricheOggi};
	mostraMisure();
	const minuti = leggiTotem();
	if (minuti === null) {
		segui();
		// (24 ott 2026) schermo da totem ma NON vero totem (es. un iPad del cliente in verticale): solo la disposizione,
		// niente ritorno all'inizio ne' cane da guardia. Il vero totem dentro il negozio si riconosce dallo user agent.
		if (!sembraTotem() || !totemVero()) return;
	} else {
		document.body.classList.add("eidos-totem");
		if (minuti <= 0) return;
	}

	const attesaMs = (minuti ?? MINUTI_PREDEFINITI) * 60 * 1000;
	let timer: number | undefined;
	const riparti = () => {
		if (timer !== undefined) window.clearTimeout(timer);
		timer = window.setTimeout(() => {
			// tastiera aperta = qualcuno sta scrivendo: si aspetta ancora
			if (document.body.classList.contains("eidos-tastiera-aperta")) {
				riparti();
				return;
			}
			// niente tocchi da un po': si ricomincia da capo (SENZA ricaricare: niente sessione nuova)
			ripartiDaCapo();
		}, attesaMs);
	};
	for (const ev of ["pointerdown", "pointermove", "touchstart", "keydown", "wheel"]) {
		window.addEventListener(ev, riparti, {passive: true});
	}
	riparti();
	caneDaGuardia();
}

/**
 * CANE DA GUARDIA del totem (28 set 2026, sera). Il totem resta acceso tutto il giorno con la STESSA sessione
 * ShapeDiver (non si ricarica piu' per non consumare crediti). Se la sessione muore (scaduta sul server, rete
 * caduta a meta' richiesta), l'SDK non la riapre da solo: ogni calcolo fallisce, i menu tornano indietro e il
 * totem sembra "bloccato". Qui si controlla:
 *  - un calcolo che resta "in corso" per piu' di 90 secondi (richiesta senza risposta) -> ricarica;
 *  - due errori di sessione di seguito senza un calcolo riuscito in mezzo -> ricarica.
 * La ricarica costa UNA sessione, ma un totem morto costa un cliente. Non piu' di una ricarica ogni 5 minuti.
 */
const MAX_RICARICHE_GIORNO = 10;
const CHIAVE_GIORNO = "eidos-ricariche-giorno";

function oggi(): string {
	return new Date().toISOString().slice(0, 10);
}

/** quante ricariche automatiche ha gia' fatto il totem oggi (localStorage: resiste alle ricariche) */
function ricaricheOggi(): number {
	try {
		const [g, n] = (localStorage.getItem(CHIAVE_GIORNO) ?? "").split("|");
		return g === oggi() ? parseInt(n, 10) || 0 : 0;
	} catch {
		return 0;
	}
}

/** avviso a tutto schermo: il totem ha bisogno di una ricarica a mano (si ricarica toccando l'avviso) */
function mostraAvvisoRicarica(): void {
	if (document.getElementById("eidos-avviso-ricarica")) return;
	const box = document.createElement("div");
	box.id = "eidos-avviso-ricarica";
	box.setAttribute(
		"style",
		"position:fixed;left:0;right:0;bottom:0;z-index:99999;background:#134D5D;color:#fff;font-family:Montserrat,sans-serif;" +
			"font-size:20px;line-height:1.4;text-align:center;padding:22px 24px;box-shadow:0 -4px 16px rgba(0,0,0,.25);cursor:pointer",
	);
	box.textContent = "Il configuratore ha bisogno di essere ricaricato. Tocca qui per ricaricare.";
	box.addEventListener("click", () => window.location.reload());
	document.body.appendChild(box);
}

/**
 * ?misura=1 : mostra in un angolo le misure dello schermo (larghezza x altezza in px CSS, densita', schermo
 * fisico). Serve per leggere le misure del totem vero e ricrearle sul computer per le prove.
 */
function mostraMisure(): void {
	try {
		if (new URLSearchParams(window.location.search).get("misura") !== "1") return;
	} catch {
		return;
	}
	const box = document.createElement("div");
	box.setAttribute(
		"style",
		"position:fixed;left:8px;bottom:8px;z-index:99999;background:rgba(19,77,93,.92);color:#fff;font:14px/1.4 monospace;padding:8px 10px;border-radius:8px",
	);
	// (5 ott sera) diagnostica completa per il telefono di Davide: regola CSS del telefono attiva?, logo, prezzo, 3D, browser
	const r = (el: Element | null) => {
		if (!el) return "-";
		const b = el.getBoundingClientRect();
		return `${Math.round(b.left)},${Math.round(b.top)} ${Math.round(b.width)}x${Math.round(b.height)}`;
	};
	const aggiorna = () => {
		let telefonoCss = "?";
		try {
			telefonoCss = window.matchMedia("(max-width: 599px)").matches ? "si" : "no";
		} catch {
			/* ignora */
		}
		const logo = document.querySelector('[class*="_appShellGridAreaMain_"] .mantine-Image-root, .mantine-Stack-root > .mantine-Image-root');
		const prezzo = document.querySelector('.mantine-Group-root:has(> .mantine-Paper-root [class*="markdownNormalize"])');
		const canvas = Array.from(document.querySelectorAll("canvas")).filter((c) => !c.classList.contains("mapboxgl-canvas")).sort((a, b) => b.clientWidth * b.clientHeight - a.clientWidth * a.clientHeight)[0];
		const main = document.querySelector('[class*="_appShellMain_"]');
		box.textContent =
			`finestra ${window.innerWidth}x${window.innerHeight} px | densita' ${window.devicePixelRatio} | schermo ${window.screen.width}x${window.screen.height} | ` +
			`regola telefono (<600px): ${telefonoCss} | totem: ${document.body.classList.contains("eidos-totem") ? "si" : "no"} | ` +
			`logo ${r(logo)} max-width ${logo ? getComputedStyle(logo).maxWidth : "-"} | prezzo ${r(prezzo)} | 3D ${r(canvas?.parentElement ?? canvas ?? null)} canvas ${canvas ? canvas.width + "x" + canvas.height : "-"} | ` +
			`righe griglia: ${main ? getComputedStyle(main).gridTemplateRows.split(" ").map((v) => Math.round(parseFloat(v))).join("/") : "-"} | ${navigator.userAgent.slice(0, 90)}`;
	};
	aggiorna();
	window.addEventListener("resize", aggiorna);
	window.setInterval(aggiorna, 2000);
	document.body.appendChild(box);
}

function caneDaGuardia(): void {
	const CHIAVE = "eidos-ricarica";
	const ricarica = (motivo: string) => {
		try {
			const ultima = parseInt(sessionStorage.getItem(CHIAVE) ?? "0", 10);
			if (Date.now() - ultima < 5 * 60 * 1000) return;
		} catch {
			/* ignora */
		}
		// TETTO GIORNALIERO (Davide, 28 set): ogni ricarica = una sessione ShapeDiver = crediti. Dopo
		// MAX_RICARICHE_GIORNO ricariche automatiche nello stesso giorno non si ricarica piu' da soli:
		// compare un avviso e la pagina si ricarica solo se una persona tocca l'avviso.
		if (ricaricheOggi() >= MAX_RICARICHE_GIORNO) {
			mostraAvvisoRicarica();
			return;
		}
		try {
			sessionStorage.setItem(CHIAVE, String(Date.now()));
			localStorage.setItem(CHIAVE_GIORNO, oggi() + "|" + (ricaricheOggi() + 1));
		} catch {
			/* ignora */
		}
		console.warn("Totem Eidos: ricarico la pagina (" + motivo + ")");
		window.location.reload();
	};
	let errori = 0;
	let inCorsoDa = 0;
	try {
		addListener(EVENTTYPE_SESSION.SESSION_ERROR, () => {
			errori++;
			if (errori >= 2) ricarica("due errori di sessione di seguito");
		});
		addListener(EVENTTYPE_SESSION.SESSION_CUSTOMIZED, () => {
			errori = 0;
			inCorsoDa = 0; // (5 ott) un calcolo riuscito azzera anche il conteggio "fermo": tanti calcoli di fila
			// (una lettera della dedica alla volta) NON sono un blocco
		});
	} catch {
		/* eventi non disponibili */
	}
	window.setInterval(() => {
		// mentre qualcuno scrive con la tastiera non si ricarica mai
		if (document.body.classList.contains("eidos-tastiera-aperta")) {
			inCorsoDa = 0;
			return;
		}
		try {
			const {parameterChanges} = useShapeDiverStoreParameters.getState();
			const occupato = Object.values(parameterChanges).some((c) => c && c.executing);
			if (!occupato) {
				inCorsoDa = 0;
				return;
			}
			if (!inCorsoDa) inCorsoDa = Date.now();
			else if (Date.now() - inCorsoDa > 90 * 1000) ricarica("calcolo fermo da piu' di 90 secondi");
		} catch {
			/* store non pronto */
		}
	}, 15 * 1000);
}
